import { memo } from 'react';

import { clampChars } from '@/components/blocks/Message/reasoning/tool-result-parser';
import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

const DEFAULT_MAX_LINES = 60;
const DIFF_LINES_PER_SIDE = 30;

function clampLines(
  text: string,
  perSide: number
): { lines: string[]; trimmed: number } {
  const all = clampChars(text).split('\n');
  if (all.length <= perSide) return { lines: all, trimmed: 0 };
  return { lines: all.slice(0, perSide), trimmed: all.length - perSide };
}

export type CodeBlockTone = 'default' | 'subtle';

interface CodeBlockProps {
  text: string;
  tone?: CodeBlockTone;
  maxLines?: number;
}

// memo: slice + split on every observer re-render is hot during streaming.
export const CodeBlock = memo(function CodeBlock({
  text,
  tone = 'default',
  maxLines = DEFAULT_MAX_LINES,
}: CodeBlockProps) {
  const safe = clampChars(text);
  const lines = safe.split('\n');
  const truncated = lines.length > maxLines;
  const display = truncated ? lines.slice(0, maxLines).join('\n') : safe;
  return (
    <YStack className="gap-1">
      <pre
        className={cn(
          'text-[11px] font-mono rounded-lg p-2 overflow-x-auto whitespace-pre-wrap max-h-56 overflow-y-auto',
          tone === 'subtle'
            ? 'bg-grayA-2 text-foreground-muted'
            : 'bg-grayA-2 text-foreground border border-grayA-4'
        )}
      >
        {display}
      </pre>
      {truncated && (
        <span className="text-[10px] text-foreground-muted/60">
          Showing first {maxLines} of {lines.length} lines
        </span>
      )}
    </YStack>
  );
});

export interface NumberedLine {
  lineNumber?: number;
  content: string;
}

interface LineNumberedBlockProps {
  lines: NumberedLine[];
  footer?: string | null;
}

export const LineNumberedBlock = memo(function LineNumberedBlock({
  lines,
  footer,
}: LineNumberedBlockProps) {
  const maxNum = lines.reduce(
    (m, l) =>
      l.lineNumber !== undefined && l.lineNumber > m ? l.lineNumber : m,
    0
  );
  const gutterCh = String(maxNum).length;

  return (
    <div className="text-[11px] font-mono rounded-lg p-2 bg-grayA-2 text-foreground-muted max-h-56 overflow-auto">
      {lines.map((line, i) => (
        <div key={i} className="flex">
          <span
            className="select-none text-foreground-muted/50 mr-3 text-right shrink-0 tabular-nums"
            style={{ minWidth: `${gutterCh}ch` }}
          >
            {line.lineNumber ?? ''}
          </span>
          <span className="whitespace-pre-wrap break-all flex-1">
            {line.content || ' '}
          </span>
        </div>
      ))}
      {footer && <div className="text-foreground-muted/60 py-px">{footer}</div>}
    </div>
  );
});

export const DiffBlock = memo(function DiffBlock({
  oldStr,
  newStr,
}: {
  oldStr: string;
  newStr: string;
}) {
  const { lines: oldLines, trimmed: oldTrimmed } = clampLines(
    oldStr,
    DIFF_LINES_PER_SIDE
  );
  const { lines: newLines, trimmed: newTrimmed } = clampLines(
    newStr,
    DIFF_LINES_PER_SIDE
  );

  return (
    <pre className="text-[11px] font-mono rounded-lg overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto border border-grayA-4">
      {oldLines.map((line, i) => (
        <div
          key={`old-${i}`}
          className="bg-danger-3/50 text-danger-11 px-2 py-px"
        >
          <span className="select-none text-danger-11/50 mr-1">-</span>
          {line}
        </div>
      ))}
      {oldTrimmed > 0 && (
        <div className="text-foreground-muted/60 px-2 py-px">
          ... {oldTrimmed} more removed lines
        </div>
      )}
      {newLines.map((line, i) => (
        <div
          key={`new-${i}`}
          className="bg-success-3/50 text-success-11 px-2 py-px"
        >
          <span className="select-none text-success-11/50 mr-1">+</span>
          {line}
        </div>
      ))}
      {newTrimmed > 0 && (
        <div className="text-foreground-muted/60 px-2 py-px">
          ... {newTrimmed} more added lines
        </div>
      )}
    </pre>
  );
});
